import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';
import { LoaderService } from '@core/services/loader.service';
import { SKIP_LOADER } from '@core/constants/http-context.constants';
import { API_BASE_URL } from '@core/constants/api-tokens.constants';

function isApiRequest(url: string, apiBaseUrl: string): boolean {
  return url.startsWith('/api/') || url.startsWith(apiBaseUrl);
}

/**
 * A read the page draws its own skeleton for. A file download is a GET too, but the user started it
 * and has nothing else to watch, so it keeps the loader.
 */
function isSkeletonRead(req: HttpRequest<unknown>): boolean {
  const read = req.method === 'GET' || req.method === 'HEAD';
  return read && req.responseType !== 'blob' && req.responseType !== 'arraybuffer';
}

function isTranslationFile(url: string): boolean {
  return url.includes('/assets/i18n/');
}

/**
 * Interceptor: show the global loader while the user's own API actions run — saves, deletes and
 * downloads. Reads are left to the page's skeletons; i18n files and SKIP_LOADER requests are
 * skipped too. Shows the loader when the first request starts, hides it when the last completes.
 */
export const loaderInterceptor: HttpInterceptorFn = (req, next) => {
  if (
    !isApiRequest(req.url, inject(API_BASE_URL)) ||
    isTranslationFile(req.url) ||
    isSkeletonRead(req) ||
    req.context.get(SKIP_LOADER)
  ) {
    return next(req);
  }

  const loader = inject(LoaderService);
  loader.show();

  return next(req).pipe(
    finalize(() => {
      loader.hide();
    })
  );
};
